import { z } from 'zod'

const isBrowser = typeof window !== 'undefined'

const defaultAdminFlags = {
  drones: true,
  vehicles: true,
  missions: true,
  users: true,
  licenses: true,
  akkus: true,
  flights: true,
  export: true,
  storage: true,
  analytics: false,
  reports: false,
  settings: true,
}

const defaultFuehrungFlags = {
  einsatzuebersicht: true,
  ressourcen: true,
  mitarbeiter: true,
  fahrzeuge: true,
  drohnen: true,
  lageplan: false,
  reports: false,
  settings: true,
  checklisten: true,
}

const defaultPilotFlags = {
  flugbuch: true,
  einsaetze: true,
  drohnen: true,
  checklisten: true,
  dokumente: false,
  settings: true,
}

const defaultLeitstelleFlags = {
  liveview: true,
  einsatzsteuerung: true,
  ressourcen: true,
  kommunikation: true,
  protokoll: true,
  settings: true,
}

const defaultGlobalFlags = {
  maintenanceMode: false,
  registrationEnabled: false,
  debugMode: false,
  newUi: true,
}

export const featureFlagsSchema = z.object({
  admin: z.object({
    drones: z.boolean().default(true),
    vehicles: z.boolean().default(true),
    missions: z.boolean().default(true),
    users: z.boolean().default(true),
    licenses: z.boolean().default(true),
    akkus: z.boolean().default(true),
    flights: z.boolean().default(true),
    export: z.boolean().default(true),
    storage: z.boolean().default(true),
    analytics: z.boolean().default(false),
    reports: z.boolean().default(false),
    settings: z.boolean().default(true),
    checklisten: z.boolean().default(true),
  }).default({ ...defaultAdminFlags, checklisten: true }),
  fuehrung: z.object({
    einsatzuebersicht: z.boolean().default(true),
    ressourcen: z.boolean().default(true),
    mitarbeiter: z.boolean().default(true),
    fahrzeuge: z.boolean().default(true),
    drohnen: z.boolean().default(true),
    lageplan: z.boolean().default(false),
    reports: z.boolean().default(false),
    settings: z.boolean().default(true),
    checklisten: z.boolean().default(true),
  }).default(defaultFuehrungFlags),
  pilot: z.object({
    flugbuch: z.boolean().default(true),
    einsaetze: z.boolean().default(true),
    drohnen: z.boolean().default(true),
    checklisten: z.boolean().default(true),
    dokumente: z.boolean().default(false),
    settings: z.boolean().default(true),
  }).default(defaultPilotFlags),
  leitstelle: z.object({
    liveview: z.boolean().default(true),
    einsatzsteuerung: z.boolean().default(true),
    ressourcen: z.boolean().default(true),
    kommunikation: z.boolean().default(true),
    protokoll: z.boolean().default(true),
    settings: z.boolean().default(true),
  }).default(defaultLeitstelleFlags),
  global: z.object({
    maintenanceMode: z.boolean().default(false),
    registrationEnabled: z.boolean().default(false),
    debugMode: z.boolean().default(false),
    newUi: z.boolean().default(true),
  }).default(defaultGlobalFlags),
})

export type FeatureFlags = z.infer<typeof featureFlagsSchema>

let cachedFeatureFlags: FeatureFlags | null = null
let cachedBrowserConfig: Record<string, unknown> | null = null
let cachedEnv: any = null

function getDefaultFeatureFlags(): FeatureFlags {
  return featureFlagsSchema.parse({})
}

export function getFeatureFlags(): FeatureFlags {
  if (cachedFeatureFlags) return cachedFeatureFlags

  if (isBrowser) {
    try {
      const config = (window as any).PILOT_HUB_CONFIG
      console.debug('[Config] PILOT_HUB_CONFIG:', config)
      console.debug('[Config] featureFlags from config:', config?.featureFlags)
      if (config?.featureFlags) {
        // Strip Zod error artifacts before parsing
        const cleanFlags = JSON.parse(JSON.stringify(config.featureFlags))
        console.debug('[Config] cleanFlags:', cleanFlags)
        const result = featureFlagsSchema.safeParse(cleanFlags)
        console.debug('[Config] safeParse result:', result)
        if (result.success) {
          cachedFeatureFlags = result.data
          console.debug('[Config] Parsed feature flags:', cachedFeatureFlags)
          return cachedFeatureFlags
        }
        // Log but don't throw - use defaults silently
        console.debug('[Config] Feature flags parse issue, using defaults')
      }
    } catch (e) {
      console.debug('[Config] Error reading feature flags, using defaults:', e)
    }
  }

  cachedFeatureFlags = getDefaultFeatureFlags()
  return cachedFeatureFlags
}

export function isFeatureEnabled(module: keyof FeatureFlags, feature: string): boolean {
  const flags = getFeatureFlags()
  const moduleFlags = flags[module] as Record<string, boolean> | undefined
  // Fallback: if moduleFlags is undefined (parsing error), enable all admin features by default
  if (!moduleFlags && module === 'admin') {
    const adminDefaults: Record<string, boolean> = {
      drones: true, vehicles: true, missions: true, users: true,
      licenses: true, akkus: true, flights: true, export: true,
      storage: true, analytics: false, reports: false, settings: true, checklisten: true
    }
    return adminDefaults[feature] ?? false
  }
  return moduleFlags?.[feature] ?? false
}

export function getModuleConfig(module: keyof FeatureFlags) {
  const flags = getFeatureFlags()
  return flags[module] || {}
}

export function getBrowserConfig(): Record<string, unknown> {
  if (cachedBrowserConfig) return cachedBrowserConfig

  if (!isBrowser) {
    cachedBrowserConfig = {}
    return cachedBrowserConfig
  }

  try {
    const config = (window as any).PILOT_HUB_CONFIG
    if (config) {
      cachedBrowserConfig = {
        apiBaseUrl: config.apiBaseUrl || '/api/v1',
        wsBaseUrl: config.wsBaseUrl || '/ws',
        module: config.module || 'admin',
        version: config.version || '1.2.049',
        buildTimestamp: config.buildTimestamp || Date.now(),
        featureFlags: getFeatureFlags(),
      }
      return cachedBrowserConfig
    }
  } catch (e) {
    console.debug('[Config] Error reading browser config:', e)
  }

  cachedBrowserConfig = {
    apiBaseUrl: '/api/v1',
    wsBaseUrl: '/ws',
    module: 'admin',
    version: '1.2.049',
    buildTimestamp: Date.now(),
    featureFlags: getDefaultFeatureFlags(),
  }
  return cachedBrowserConfig
}

// ---------------------------------------------------------------------------
// Rollen & Funktionen — Single Source of Truth (ASB Drohnenstaffel)
// ---------------------------------------------------------------------------
export interface KatalogEintrag {
  value: string
  label: string
  beschreibung: string
}

/** Rollen (5) — Zugriffs-/Berechtigungsebene */
export const ROLLEN: KatalogEintrag[] = [
  { value: 'ADMIN',      label: 'Admin',         beschreibung: 'Administrator' },
  { value: 'FUEHRUNG',   label: 'Führungskraft', beschreibung: 'Führungskraft' },
  { value: 'PILOT',      label: 'Pilot',         beschreibung: 'Pilot' },
  { value: 'HELFER',     label: 'Helfer',        beschreibung: 'Helfer' },
  { value: 'LEITSTELLE', label: 'Leitstelle',    beschreibung: 'Leitstelle' },
]

/** Funktionen (7) — fachliche Rolle im Einsatz */
export const FUNKTIONEN: KatalogEintrag[] = [
  { value: 'GRUPPENFUEHRER',       label: 'Gruppenführer',       beschreibung: 'Gruppenführer' },
  { value: 'FUEHRUNGSASISTENT',    label: 'Führungsassistent',   beschreibung: 'Führungsassistent' },
  { value: 'FACHBERATER',          label: 'Fachberater',         beschreibung: 'Fachberater' },
  { value: 'PILOT',                label: 'Pilot',               beschreibung: 'Pilot' },
  { value: 'CO_PILOT',             label: 'Co-Pilot',            beschreibung: 'Co-Pilot' },
  { value: 'LUFTRAUM_BEOBACHTER',  label: 'Luftraumbeobachter',  beschreibung: 'Luftraumbeobachter' },
  { value: 'LUFTRAUM_KOORDINATOR', label: 'Luftraumkoordinator', beschreibung: 'Luftraumkoordinator' },
]

export const FUNKTIONEN_FARBEN: Record<string, string> = {
  GRUPPENFUEHRER: '#ea580c',      // orange
  FUEHRUNGSASISTENT: '#1d4ed8',   // blau
  FACHBERATER: '#1d4ed8',         // blau
  PILOT: '#6b7280',             // grau
  CO_PILOT: '#6b7280',            // grau
  LUFTRAUM_BEOBACHTER: '#6b7280', // grau
  LUFTRAUM_KOORDINATOR: '#6b7280',// grau
}

export function funktionenFarbe(value: string): string {
  return FUNKTIONEN_FARBEN[value] ?? '#111827'
}

export function rollenLabel(value: string): string {
  return ROLLEN.find(r => r.value === value)?.label ?? value
}

export function funktionenLabel(value: string): string {
  return FUNKTIONEN.find(f => f.value === value)?.label ?? value
}

export const moduleConfigs = {
  admin: {
    title: 'Admin-Modul • ASB Drohnenstaffel',
    subtitle: 'Verwaltung & Konfiguration',
    loginEndpoint: '/auth/login',
    redirectPath: '/dashboard',
    requiredRoles: ['ADMIN'],
    apiBase: '/api/v1',
  },
  fuehrung: {
    title: 'Führungskräfte-Modul • ASB Drohnenstaffel',
    subtitle: 'Führung & Übersicht',
    loginEndpoint: '/auth/login',
    redirectPath: '/verwaltung',
    requiredRoles: ['FUEHRUNG'],
    apiBase: '/api/v1',
  },
  pilot: {
    title: 'Piloten-Modul • ASB Drohnenstaffel',
    subtitle: 'Einsatz & Flugbuch',
    loginEndpoint: '/auth/login',
    redirectPath: '/einsatz',
    requiredRoles: ['PILOT'],
    apiBase: '/api/v1',
  },
  leitstelle: {
    title: 'Leitstellen-Modul • ASB Drohnenstaffel',
    subtitle: 'Leitstelle & Überwachung',
    loginEndpoint: '/auth/login',
    redirectPath: '/dashboard',
    requiredRoles: ['LEITSTELLE'],
    apiBase: '/api/v1',
  },
} as const

export function getModuleConfigByKey(module: keyof typeof moduleConfigs) {
  return moduleConfigs[module]
}

// Browser-safe env config
const browserEnv = {
  NODE_ENV: 'development',
  PORT: 3000,
  DATABASE_URL: undefined,
  JWT_SECRET: undefined,
  JWT_REFRESH_SECRET: undefined,
  JWT_ACCESS_EXPIRY: '15m',
  JWT_REFRESH_EXPIRY: '7d',
  CORS_ORIGIN: '*',
  LOG_LEVEL: 'info',
  STORAGE_DRIVER: 'local',
  STORAGE_LOCAL_PATH: './storage',
  S3_ENDPOINT: undefined,
  S3_REGION: undefined,
  S3_BUCKET: undefined,
  S3_ACCESS_KEY: undefined,
  S3_SECRET_KEY: undefined,
  NEXTCLOUD_URL: undefined,
  NEXTCLOUD_USER: undefined,
  NEXTCLOUD_PASS: undefined,
}

export function getEnv(): any {
  if (cachedEnv) return cachedEnv

  if (isBrowser) {
    cachedEnv = browserEnv
    return cachedEnv
  }

  // Node.js environment - use process.env with fallback
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { envSchema } = require('./env-node')
    cachedEnv = envSchema
  } catch {
    cachedEnv = browserEnv
  }

  return cachedEnv
}

// Initialize - wrapped in try/catch to prevent startup crashes
try {
  getFeatureFlags()
  if (isBrowser) {
    getBrowserConfig()
  }
} catch (e) {
  console.debug('[Config] Initialization warning:', e)
}

// Force module side effects to prevent tree-shaking
// These assignments to window ensure the module is never tree-shaken
if (isBrowser) {
  try {
    (window as any).__PILOT_HUB_CONFIG_LOADED__ = true
    ;(window as any).__PILOT_HUB_CONFIG_VERSION__ = '1.2.049'
  } catch {}
}

// Export a side-effect function that must be called
export function __ensureConfigLoaded__(): boolean {
  return true
}

// Force usage to prevent tree-shaking
if (typeof window !== 'undefined') {
  (window as any).__PILOT_HUB_CONFIG_EXPORTS__ = {
    isFeatureEnabled,
    getFeatureFlags,
    getModuleConfig,
    getBrowserConfig,
    getDefaultFeatureFlags,
  }
}